/**
 * Life4Earth V2 — Gantt Task Role Assignments API Adapter
 *
 * Junction table bridging Gantt tasks to Project Role Slots.
 * Supports RACI responsibility types and bulk operations.
 *
 * Endpoint: /api/projects/{guid}/task-role-assignments
 *
 * Epic: DEV-625 prerequisite (Role-Based Gantt Task Assignment)
 */

import { apiFetch } from "./client";

// ── Responsibility Types (RACI) ─────────────────────────────────

export const RESPONSIBILITY_TYPES = [
  "responsible",
  "accountable",
  "consulted",
  "informed",
] as const;

export type ResponsibilityType = (typeof RESPONSIBILITY_TYPES)[number];

// ── API Response Types ──────────────────────────────────────────

export interface TaskRoleAssignmentResponse {
  id: string;
  ganttTaskId: string;
  roleSlotGuid: string;
  responsibilityType: string;
  sortOrder: number;
  createdBy: string | null;
  createdAt: string;
}

/**
 * Enriched response returned by the project-level listing.
 * Includes denormalized role slot info so the frontend doesn't
 * need a separate fetch for display.
 */
export interface TaskRoleAssignmentEnriched extends TaskRoleAssignmentResponse {
  roleLabel: string;
  roleCategory: string;
  rolePhase: string;
}

// ── Request Types ───────────────────────────────────────────────

export interface CreateTaskRoleAssignmentRequest {
  ganttTaskId: string;
  roleSlotGuid: string;
  responsibilityType?: ResponsibilityType; // defaults to "responsible"
}

export interface BulkCreateTaskRoleAssignmentRequest {
  assignments: CreateTaskRoleAssignmentRequest[];
}

export interface BulkDeleteTaskRoleAssignmentRequest {
  ids: string[];
}

// ── Engagement Types ────────────────────────────────────────────

/**
 * A Gantt task surfaced in the user's Engagements via role assignment.
 */
export interface EngagementTask {
  ganttTaskId: string;
  taskName: string;
  startDate: string | null;
  endDate: string | null;
  percentComplete: number;
  isMilestone: boolean;
  responsibilityType: string;
  roleSlotGuid: string;
  roleLabel: string;
  projectGuid: string;
  projectName: string;
  /** Comma-separated predecessor taskId numbers, "" if none. Used for blocked detection. */
  predecessors: string;
  /** Task notes/comments from the Gantt schedule. */
  notes: string;
}

// ── Internal Lookup Type ────────────────────────────────────────

/**
 * Map of ganttTaskId → array of enriched role assignments.
 * Built client-side from the project-level listing for O(1) lookup
 * when rendering the Gantt grid.
 */
export type TaskRoleAssignmentMap = Map<string, TaskRoleAssignmentEnriched[]>;

/**
 * Build a task-indexed lookup map from a flat array of enriched assignments.
 */
export function buildTaskRoleAssignmentMap(
  assignments: TaskRoleAssignmentEnriched[]
): TaskRoleAssignmentMap {
  const map: TaskRoleAssignmentMap = new Map();
  for (const a of assignments) {
    const key = a.ganttTaskId.toUpperCase();
    const existing = map.get(key);
    if (existing) {
      existing.push(a);
    } else {
      map.set(key, [a]);
    }
  }
  return map;
}

// ── API Functions ───────────────────────────────────────────────

/**
 * List all task-role assignments for a project (enriched with role info).
 */
export async function fetchTaskRoleAssignments(
  projectGuid: string
): Promise<TaskRoleAssignmentEnriched[]> {
  return apiFetch<TaskRoleAssignmentEnriched[]>(
    `/api/projects/${projectGuid}/task-role-assignments`
  );
}

/**
 * List task-role assignments for a specific task.
 */
export async function fetchTaskRoleAssignmentsByTask(
  projectGuid: string,
  ganttTaskId: string
): Promise<TaskRoleAssignmentEnriched[]> {
  return apiFetch<TaskRoleAssignmentEnriched[]>(
    `/api/projects/${projectGuid}/task-role-assignments?taskId=${encodeURIComponent(ganttTaskId)}`
  );
}

/**
 * List task-role assignments for a specific role slot.
 */
export async function fetchTaskRoleAssignmentsByRole(
  projectGuid: string,
  roleSlotGuid: string
): Promise<TaskRoleAssignmentEnriched[]> {
  return apiFetch<TaskRoleAssignmentEnriched[]>(
    `/api/projects/${projectGuid}/task-role-assignments?roleSlotGuid=${encodeURIComponent(roleSlotGuid)}`
  );
}

/**
 * Assign a role to a single task.
 */
export async function createTaskRoleAssignment(
  projectGuid: string,
  req: CreateTaskRoleAssignmentRequest
): Promise<TaskRoleAssignmentResponse> {
  return apiFetch<TaskRoleAssignmentResponse>(
    `/api/projects/${projectGuid}/task-role-assignments`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

/**
 * Bulk assign roles to tasks (multi-select workflow).
 * Sends an array of {ganttTaskId, roleSlotGuid, responsibilityType}.
 */
export async function bulkCreateTaskRoleAssignments(
  projectGuid: string,
  req: BulkCreateTaskRoleAssignmentRequest
): Promise<TaskRoleAssignmentResponse[]> {
  return apiFetch<TaskRoleAssignmentResponse[]>(
    `/api/projects/${projectGuid}/task-role-assignments/bulk`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

/**
 * Update the responsibility type on an existing task-role assignment.
 */
export async function updateTaskRoleAssignmentType(
  projectGuid: string,
  assignmentId: string,
  responsibilityType: ResponsibilityType
): Promise<TaskRoleAssignmentEnriched> {
  return apiFetch<TaskRoleAssignmentEnriched>(
    `/api/projects/${projectGuid}/task-role-assignments/${assignmentId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ responsibilityType }),
    }
  );
}

/**
 * Remove a single task-role assignment.
 */
export async function deleteTaskRoleAssignment(
  projectGuid: string,
  assignmentId: string
): Promise<void> {
  return apiFetch<void>(
    `/api/projects/${projectGuid}/task-role-assignments/${assignmentId}`,
    { method: "DELETE" }
  );
}

/**
 * Bulk remove task-role assignments.
 */
export async function bulkDeleteTaskRoleAssignments(
  projectGuid: string,
  req: BulkDeleteTaskRoleAssignmentRequest
): Promise<void> {
  return apiFetch<void>(
    `/api/projects/${projectGuid}/task-role-assignments/bulk`,
    {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

// ── Engagement Query ────────────────────────────────────────────

/**
 * Fetch all Gantt tasks assigned to a user via their active role assignments,
 * across all projects. Grouped by project on the backend.
 */
export async function fetchEngagementTasks(
  userGuid: string
): Promise<EngagementTask[]> {
  return apiFetch<EngagementTask[]>(
    `/api/users/${userGuid}/engagements/tasks`
  );
}
